import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe } from '@/hooks/useMe';
import { authApi } from '@/api/endpoints';
import { tokenStore } from '@/store/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatCents } from '@/utils/money';
import { ActivityCard } from '@/features/account/ActivityCard';
import { NotificationsCard } from '@/features/account/NotificationsCard';
import { SecurityCard } from '@/features/account/SecurityCard';
import { SessionsCard } from '@/features/account/SessionsCard';
import { AccountMenu } from '@/features/account/AccountMenu';

const UNVERIFIED_PILL = { label: 'Unverified', cls: 'text-white/55 bg-white/[0.06] border-white/15' };
const KYC_PILL: Record<string, { label: string; cls: string }> = {
  verified:   { label: 'Verified', cls: 'text-brand-green bg-brand-green/10 border-brand-green/30' },
  pending:    { label: 'Pending',  cls: 'text-amber-400 bg-amber-400/10 border-amber-400/30' },
  unverified: UNVERIFIED_PILL,
  rejected:   { label: 'Rejected', cls: 'text-red-400 bg-red-400/10 border-red-400/30' },
};

export function AccountPage() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleLogout = async () => {
    try { await authApi.logout(); } catch { /* ignore — clear client-side anyway */ }
    tokenStore.clear();
    navigate('/login');
  };

  const firstName = me?.user.firstName ?? '';
  const email     = me?.user.email ?? '—';
  const kyc       = me?.user.kycStatus ?? 'unverified';
  const pill      = KYC_PILL[kyc] ?? UNVERIFIED_PILL;
  const refCode   = me?.user.referralCode ?? '';
  const memberSince = me?.user.createdAt
    ? new Date(me.user.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  const savings = me?.balances.savingsCents ?? 0;
  const balance = me?.balances.balanceCents ?? 0;

  const copyRef = async () => {
    if (!refCode) return;
    try {
      await navigator.clipboard.writeText(refCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — no-op */ }
  };

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      {/* Header */}
      <div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-teal">Account</span>
        <h1 className="font-display font-extrabold text-3xl sm:text-[34px] tracking-[-0.02em] mt-1">
          Your account
        </h1>
      </div>

      {/* Profile */}
      <Card>
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 rounded-2xl grid place-items-center bg-gradient-to-br from-brand-teal to-brand-mint text-brand-navy font-display font-extrabold text-2xl shadow-glow">
            {(firstName?.[0] ?? email[0] ?? 'U').toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-lg leading-tight truncate">{firstName || 'Welcome'}</div>
            <div className="text-sm text-white/50 truncate">{email}</div>
            <div className="text-xs text-white/35 mt-0.5">Member since {memberSince}</div>
          </div>
          <span className={`shrink-0 text-[11px] font-semibold rounded-full px-2.5 py-1 border ${pill.cls}`}>
            {pill.label}
          </span>
        </div>

        {kyc !== 'verified' && (
          <button
            onClick={() => navigate('/deposit')}
            className="mt-4 w-full text-left rounded-xl border border-brand-teal/25 bg-brand-teal/[0.06] px-4 py-3 text-sm text-white/75 hover:bg-brand-teal/10 transition"
          >
            <span className="text-brand-teal font-semibold">Verify your identity</span> to unlock deposits →
          </button>
        )}
      </Card>

      {/* Balances snapshot */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-brand-green/20 bg-gradient-to-br from-[#0e2e1c] to-[#082014] p-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-brand-green/80">Savings (locked)</div>
          <div className="mt-1 font-display text-2xl font-extrabold tabular-nums">{formatCents(savings)}</div>
        </div>
        <div className="rounded-2xl border border-brand-teal/20 bg-gradient-to-br from-[#102f3d] to-[#0a1c2c] p-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-brand-teal/90">Play Balance</div>
          <div className="mt-1 font-display text-2xl font-extrabold tabular-nums">{formatCents(balance)}</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button onClick={() => navigate('/deposit')} className="w-full">Add funds</Button>
        <Button variant="secondary" onClick={() => navigate('/savings')} className="w-full">Savings &amp; withdraw</Button>
      </div>

      {/* The prototype's account menu — every section of the account, one row
          each, in the order the stakeholder asked for (2026-10-13). The cards
          below are the detail behind the rows that live on this page. */}
      <AccountMenu isStaff={Boolean(me?.user.role)} />

      {/* Details */}
      <Card title="Details" id="referral">
        <dl className="flex flex-col text-sm">
          <div className="flex items-center justify-between py-2.5 border-b border-white/[0.07]">
            <dt className="text-white/50">KYC status</dt>
            <dd className="capitalize">{kyc}</dd>
          </div>
          <div className="flex items-center justify-between py-2.5 border-b border-white/[0.07]">
            <dt className="text-white/50">Referral code</dt>
            <dd className="flex items-center gap-2">
              <span className="font-mono tracking-wide">{refCode || '—'}</span>
              {refCode && (
                <button
                  onClick={copyRef}
                  className="text-xs rounded-md px-2 py-0.5 border border-white/15 text-white/60 hover:bg-white/10 hover:text-white transition"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              )}
            </dd>
          </div>
          {me?.user.referredByFirstName && (
            <div className="flex items-center justify-between py-2.5">
              <dt className="text-white/50">Referred by</dt>
              <dd>{me.user.referredByFirstName}</dd>
            </div>
          )}
        </dl>
      </Card>

      <div id="activity"><ActivityCard /></div>

      <SecurityCard />

      <SessionsCard />

      <NotificationsCard />

      <Card title="Help &amp; support">
        <p className="text-sm text-white/55">
          Questions about a bet, a withdrawal or your account? Email{' '}
          <a href="mailto:support@puntsave.com.au" className="text-brand-teal hover:underline">
            support@puntsave.com.au
          </a>
          .
        </p>
        <p className="mt-3 text-xs text-white/35">
          Gambling help is available 24/7 on 1800 858 858 or at gamblinghelponline.org.au.
        </p>
      </Card>

      <Button variant="danger" onClick={handleLogout} className="w-full">Sign out</Button>
    </div>
  );
}
